import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Plus, Pencil, Trash2, FileWarning } from 'lucide-react';

import { useAbsenceStore } from '../../store/useAbsenceStore';
import { useAbsenceTypeStore } from '../../store/useAbsenceTypeStore';
import { useAuthStore } from '../../store/useAuthStore';
import { useVacationStore } from '../../store/useVacationStore';
import { useOvertimeStore } from '../../store/useOvertimeStore';
import { Card } from '../../components/Shared/Card';
import { Button } from '../../components/Shared/Button';
import { Loading } from '../../components/Shared/Loading';
import { EmptyState } from '../../components/Shared/EmptyState';
import { Badge } from '../../components/Shared/Badge';
import { SlideOver, SlideOverFooter } from '../../components/Shared/SlideOver';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog';
import { FormField } from '../../components/Shared/FormField';
import { TextArea } from '../../components/Shared/TextArea';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../../components/Shared/Table';
import { CertificateCell } from './components/CertificateCell';
import { WorkingDaysPreview } from './components/WorkingDaysPreview';
import { RoundedDaysHint } from './components/RoundedDaysHint';
import { useWorkingDaysPreview } from '../../hooks/useWorkingDaysPreview';
import { chargeableDays, formatDays } from '../../utils/absenceDays';
import type { AbsenceListDto, CreateAbsenceRequest, UpdateAbsenceRequest } from '../../types';

function formatDate(dateString?: string | null) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('de-DE');
}

function getStatusBadge(status?: string | null, t?: (key: string) => string) {
  if (!t) return null;
  switch (status) {
    case 'Approved':
      return <Badge variant="success">{t('absences:status.approved')}</Badge>;
    case 'Pending':
      return <Badge variant="warning">{t('absences:status.pending')}</Badge>;
    case 'Rejected':
      return <Badge variant="danger">{t('absences:status.rejected')}</Badge>;
    default:
      return <Badge variant="default">{t('absences:status.unknown')}</Badge>;
  }
}

/** Hours as a signed h:mm string, e.g. +12:30 or −3:15. */
function formatSignedHours(hours: number): string {
  const sign = hours < 0 ? '−' : '+';
  const minutes = Math.round(Math.abs(hours) * 60);
  return `${sign}${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')} h`;
}

export default function MyAbsencesPage() {
  const { t } = useTranslation(['absences', 'common']);
  const { employee } = useAuthStore();
  const {
    myAbsences: absences,
    error,
    fetchMyAbsences,
    getAbsenceById,
    createMyAbsence,
    updateAbsence,
    deleteAbsence,
    uploadCertificate,
    openCertificate,
    uploadingCertificateFor,
  } = useAbsenceStore();
  const { absenceTypes, fetchAbsenceTypes } = useAbsenceTypeStore();
  const { myBalance, fetchMyBalance } = useVacationStore();
  // Zeitausgleich comes out of the overtime account — shown next to the vacation account.
  const myOvertime = useOvertimeStore((s) => s.myBalance);
  const fetchMyOvertime = useOvertimeStore((s) => s.fetchMyBalance);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState<AbsenceListDto | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [absenceToDelete, setAbsenceToDelete] = useState<{ id: string; name: string } | null>(null);

  const [formData, setFormData] = useState({
    absenceTypeId: '',
    startDate: '',
    endDate: '',
    notes: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const daysPreview = useWorkingDaysPreview(formData.startDate, formData.endDate);
  const selectedType = absenceTypes.find((tp) => tp.absenceTypeId === formData.absenceTypeId);
  // why: the rest after this request only makes sense for a new request on a type that draws on
  // vacation; an edited request is already part of the pending days.
  const remainingBefore =
    !editingAbsence && selectedType?.countsAgainstVacation && myBalance
      ? myBalance.remainingDays - myBalance.pendingDays
      : null;

  useEffect(() => {
    const load = async () => {
      try {
        // Der Server scopet /me/absences auf den angemeldeten Mitarbeiter — kein employeeId-Filter nötig.
        await Promise.all([fetchAbsenceTypes(), fetchMyAbsences(), fetchMyBalance(), fetchMyOvertime()]);
      } finally {
        setInitialLoadComplete(true);
      }
    };
    load();
  }, [fetchMyAbsences, fetchAbsenceTypes, fetchMyBalance, fetchMyOvertime]);

  const filtered = absences.filter((a) => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'pending') return a.approvalStatus === 'Pending';
    if (filterStatus === 'approved') return a.approvalStatus === 'Approved';
    if (filterStatus === 'rejected') return a.approvalStatus === 'Rejected';
    return true;
  });

  const stats = {
    total: absences.length,
    approved: absences.filter((a) => a.approvalStatus === 'Approved').length,
    pending: absences.filter((a) => a.approvalStatus === 'Pending').length,
    rejected: absences.filter((a) => a.approvalStatus === 'Rejected').length,
  };

  const missingCertificates = absences.filter((a) => a.certificateStatus === 'Missing').length;

  // why: kein fetchMyBalance danach — eine Krankmeldung wird nicht vom Urlaub abgezogen,
  // die Bestätigung ändert am Konto also nichts.
  const handleCertificateUpload = async (absenceId: string, file: File) => {
    try {
      await uploadCertificate(absenceId, file);
    } catch {
      // Fehlermeldung kommt aus dem Store-Toast.
    }
  };

  const openCreateDialog = () => {
    setEditingAbsence(null);
    setFormData({
      absenceTypeId: '',
      startDate: '',
      endDate: '',
      notes: '',
    });
    setIsSlideOverOpen(true);
  };

  const openEditDialog = async (absence: AbsenceListDto) => {
    setEditingAbsence(absence);
    const full = await getAbsenceById(absence.absenceId || '');
    setFormData({
      absenceTypeId: full?.absenceTypeId || '',
      startDate: absence.startDate || '',
      endDate: absence.endDate || '',
      notes: full?.notes || '',
    });
    setIsSlideOverOpen(true);
  };

  const openDeleteDialog = (absence: AbsenceListDto) => {
    setAbsenceToDelete({
      id: absence.absenceId || '',
      name: `${absence.absenceTypeName} (${formatDate(absence.startDate)} – ${formatDate(absence.endDate)})`,
    });
    setIsDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!employee?.id || !formData.absenceTypeId || !formData.startDate || !formData.endDate) return;

    setIsSaving(true);
    try {
      if (editingAbsence) {
        const update: UpdateAbsenceRequest = {
          absenceTypeId: formData.absenceTypeId,
          startDate: formData.startDate,
          endDate: formData.endDate,
          notes: formData.notes || undefined,
        };
        // Self-Service-Endpunkt: nur eigene, offene Anträge (FAFD-8).
        await updateAbsence(editingAbsence.absenceId || '', update, true);
      } else {
        // Self-Service-Endpunkt: der Server setzt den Mitarbeiter und prüft den Resturlaub.
        const create: Omit<CreateAbsenceRequest, 'employeeId'> = {
          absenceTypeId: formData.absenceTypeId,
          startDate: formData.startDate,
          endDate: formData.endDate,
          notes: formData.notes || undefined,
        };
        await createMyAbsence(create);
      }
      await fetchMyBalance();
      setIsSlideOverOpen(false);
    } catch (err) {
      console.error('Failed to save absence:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!absenceToDelete) return;
    try {
      await deleteAbsence(absenceToDelete.id, true);
      await fetchMyBalance();
    } catch {
      // The store already shows the translated error.
    } finally {
      setIsDeleteDialogOpen(false);
      setAbsenceToDelete(null);
    }
  };

  if (!initialLoadComplete) {
    return <Loading />;
  }

  const canEdit = (a: AbsenceListDto) => a.approvalStatus === 'Pending';

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('absences:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('absences:subtitle.user')}
            </p>
          </div>
          <Button
            onClick={openCreateDialog}
            leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
            className="w-full sm:w-auto"
          >
            {t('absences:buttons.newAbsenceUser')}
          </Button>
        </div>
      </div>

      {/* Urlaubskonto */}
      {myBalance && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400">
                {t('absences:vacation.remaining')}
              </div>
              <div className="mt-1 flex items-baseline gap-1.5">
                <span className="font-mono-num text-[32px] sm:text-[36px] font-semibold tabular-nums leading-none text-slate-900">
                  {formatDays(myBalance.remainingDays)}
                </span>
                <span className="text-[13px] text-slate-500">{t('absences:vacation.days')}</span>
                <span className="self-center">
                  <RoundedDaysHint value={myBalance.remainingDays} unrounded={myBalance.remainingDaysUnrounded} />
                </span>
              </div>
              <p className="text-[12px] text-slate-500 mt-1.5 tabular-nums">
                {t('absences:vacation.year', {
                  start: formatDate(myBalance.vacationYearStart),
                  end: formatDate(myBalance.vacationYearEnd),
                })}
              </p>
            </div>

            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 sm:text-right">
              <BalanceFigure
                label={t('absences:vacation.entitled')}
                value={formatDays(myBalance.entitledDays)}
                info={<RoundedDaysHint value={myBalance.entitledDays} unrounded={myBalance.entitledDaysUnrounded} />}
              />
              <BalanceFigure
                label={t('absences:vacation.carriedOver')}
                value={formatDays(myBalance.carriedOverDays)}
                hint={
                  myBalance.carryOverExpired
                    ? t('absences:vacation.carryOverExpired')
                    : myBalance.carryOverExpiresAt && myBalance.carriedOverDays > 0
                      ? t('absences:vacation.carryOverExpires', {
                          date: formatDate(myBalance.carryOverExpiresAt),
                        })
                      : undefined
                }
                hintTone={myBalance.carryOverExpired ? 'muted' : 'amber'}
              />
              <BalanceFigure label={t('absences:vacation.used')} value={formatDays(myBalance.usedDays)} />
              <BalanceFigure label={t('absences:vacation.pending')} value={formatDays(myBalance.pendingDays)} />
            </dl>
          </div>
        </div>
      )}

      {/* Überstundenkonto — Grundlage für Zeitausgleich */}
      {myOvertime && (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 sm:px-5 flex flex-wrap items-baseline justify-between gap-2">
          <div className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400">
            {t('absences:overtime.balance')}
          </div>
          <div className="flex items-baseline gap-2">
            <span
              className={`font-mono-num text-[20px] font-semibold tabular-nums ${
                myOvertime.balanceHours < 0 ? 'text-red-600' : 'text-slate-900'
              }`}
            >
              {formatSignedHours(myOvertime.balanceHours)}
            </span>
            <span className="text-[12px] text-slate-500">{t('absences:overtime.hint')}</span>
          </div>
        </div>
      )}

      {/* Fehlende Arztbestätigungen */}
      {missingCertificates > 0 && (
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <FileWarning className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" strokeWidth={2} />
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-amber-900">
              {missingCertificates === 1
                ? t('absences:certificate.missingBannerOne')
                : t('absences:certificate.missingBannerOther', { count: missingCertificates })}
            </p>
            <p className="text-[11px] text-amber-700 mt-0.5">{t('absences:certificate.hint')}</p>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard label={t('absences:statistics.total')} value={stats.total} />
        <KpiCard label={t('absences:statistics.approved')} value={stats.approved} tone="emerald" />
        <KpiCard label={t('absences:statistics.pending')} value={stats.pending} tone="amber" />
        <KpiCard label={t('absences:statistics.rejected')} value={stats.rejected} tone="red" />
      </div>

      {/* Filter */}
      <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-medium uppercase tracking-wider text-slate-500">
            {t('absences:filters.statusLabel')}
          </span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label={t('absences:filters.statusLabel')}
            className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
          >
            <option value="all">{t('absences:filters.allStatus')}</option>
            <option value="approved">{t('absences:status.approved')}</option>
            <option value="pending">{t('absences:status.pending')}</option>
            <option value="rejected">{t('absences:status.rejected')}</option>
          </select>
        </div>
      </div>

      {/* Table or Empty State */}
      {error && initialLoadComplete ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('absences:empty.errorTitle')}
            description={error}
            onRetry={() => fetchMyAbsences()}
          />
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Calendar className="w-12 h-12" strokeWidth={1.75} />}
          title={filterStatus !== 'all' ? t('absences:empty.noResults') : t('absences:empty.noAbsences')}
          description={
            filterStatus !== 'all'
              ? t('absences:empty.noResultsDescription')
              : t('absences:empty.noAbsencesDescription')
          }
          action={
            filterStatus === 'all' ? (
              <Button
                onClick={openCreateDialog}
                leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              >
                {t('absences:buttons.createAbsence')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card noPadding>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>{t('absences:table.absence')}</TableHeader>
                <TableHeader className="hidden sm:table-cell">{t('absences:table.from')}</TableHeader>
                <TableHeader className="hidden sm:table-cell">{t('absences:table.to')}</TableHeader>
                <TableHeader className="hidden md:table-cell">{t('absences:table.workingDays')}</TableHeader>
                <TableHeader>{t('absences:table.status')}</TableHeader>
                <TableHeader>{t('absences:table.certificate')}</TableHeader>
                <TableHeader>{t('absences:table.actions')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((absence) => (
                <TableRow key={absence.absenceId}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {absence.absenceTypeColor && (
                        <div
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: absence.absenceTypeColor }}
                        />
                      )}
                      <span className="text-[13px] font-medium text-slate-900">
                        {absence.absenceTypeName}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 sm:hidden tabular-nums">
                      {formatDate(absence.startDate)} – {formatDate(absence.endDate)}
                      {' · '}{formatDays(chargeableDays(absence))} {t('absences:table.workingDays')}
                    </div>
                    {absence.approvalStatus === 'Rejected' && absence.rejectionReason && (
                      <p className="text-[11px] text-red-600 mt-1">
                        {t('absences:table.rejectionReason')}: {absence.rejectionReason}
                      </p>
                    )}
                    {absence.substituteEmployeeName && (
                      <p className="text-[11px] text-slate-500 mt-1">
                        {t('absences:substitute.coveredBy', { name: absence.substituteEmployeeName })}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-[13px] text-slate-700 tabular-nums">
                    {formatDate(absence.startDate)}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-[13px] text-slate-700 tabular-nums">
                    {formatDate(absence.endDate)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-[13px] text-slate-700 tabular-nums">
                    {/* why: Abwesenheiten von vor dem Urlaubskonto haben keinen Arbeitstage-Wert.
                        Ersatzweise Kalendertage anzuzeigen wäre falsch — das Konto verrechnet
                        andere Zahlen. Lieber nichts zeigen. */}
                    {absence.workingDays != null ? formatDays(absence.workingDays) : '–'}
                  </TableCell>
                  <TableCell>{getStatusBadge(absence.approvalStatus, t)}</TableCell>
                  <TableCell>
                    <CertificateCell
                      status={absence.certificateStatus}
                      documentId={absence.certificateDocumentId}
                      uploading={uploadingCertificateFor === absence.absenceId}
                      onUpload={(file) => handleCertificateUpload(absence.absenceId, file)}
                      onView={() =>
                        absence.certificateDocumentId && openCertificate(absence.certificateDocumentId)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 sm:gap-2">
                      {canEdit(absence) ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(absence)}
                            title={t('absences:buttons.edit')}
                            aria-label={t('absences:buttons.edit')}
                            className="p-1.5"
                          >
                            <Pencil className="w-4 h-4" strokeWidth={2} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(absence)}
                            title={t('absences:buttons.delete')}
                            aria-label={t('absences:buttons.delete')}
                            className="p-1.5"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" strokeWidth={2} />
                          </Button>
                        </>
                      ) : (
                        <span className="text-[12px] text-slate-400">–</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create / Edit SlideOver */}
      <SlideOver
        isOpen={isSlideOverOpen}
        onClose={() => setIsSlideOverOpen(false)}
        title={editingAbsence ? t('absences:form.titleEdit') : t('absences:form.titleNew')}
        size="lg"
        footer={
          <SlideOverFooter
            onCancel={() => setIsSlideOverOpen(false)}
            onConfirm={handleSave}
            confirmText={editingAbsence ? t('absences:buttons.save') : t('absences:buttons.create')}
            cancelText={t('absences:buttons.cancel')}
            loading={isSaving}
          />
        }
      >
        <div className="space-y-4">
          <FormField label={t('absences:form.absenceTypeLabel')} required>
            <select
              value={formData.absenceTypeId}
              onChange={(e) => setFormData({ ...formData, absenceTypeId: e.target.value })}
              required
              aria-label={t('absences:form.absenceTypeLabel')}
              className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
            >
              <option value="">{t('absences:form.absenceTypeSelect')}</option>
              {absenceTypes.filter((tp) => tp.isActive).map((type) => (
                <option key={type.absenceTypeId} value={type.absenceTypeId}>
                  {type.name}
                </option>
              ))}
            </select>
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label={t('absences:form.fromLabel')}
              required
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className="tabular-nums"
            />
            <FormField
              label={t('absences:form.toLabel')}
              required
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              className="tabular-nums"
            />
          </div>

          <WorkingDaysPreview
            workingDays={daysPreview.workingDays}
            loading={daysPreview.loading}
            remainingVacation={remainingBefore}
          />

          <TextArea
            label={t('absences:form.notesLabel')}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={4}
            placeholder={t('absences:form.notesPlaceholder')}
          />
        </div>
      </SlideOver>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setAbsenceToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('absences:deleteDialog.title')}
        message={`${t('absences:deleteDialog.message')}\n\n${absenceToDelete?.name}\n\n${t('common:confirmations.cannotUndo')}`}
        confirmText={t('absences:buttons.delete')}
        cancelText={t('absences:buttons.cancel')}
        variant="danger"
      />
    </div>
  );
}

function BalanceFigure({
  label,
  value,
  info,
  hint,
  hintTone = 'muted',
}: {
  label: string;
  value: string;
  /** Optional info button rendered next to the value (e.g. the rounding hint). */
  info?: ReactNode;
  hint?: string;
  hintTone?: 'muted' | 'amber';
}) {
  return (
    <div>
      <dt className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400">
        {label}
      </dt>
      <dd className="mt-0.5 flex items-center gap-1 sm:justify-end font-mono-num text-[15px] font-medium tabular-nums text-slate-900">
        {value}
        {info}
      </dd>
      {hint && (
        <p className={`text-[11px] mt-0.5 ${hintTone === 'amber' ? 'text-amber-600' : 'text-slate-400'}`}>
          {hint}
        </p>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone = 'slate',
}: {
  label: string;
  value: number;
  tone?: 'slate' | 'emerald' | 'amber' | 'red';
}) {
  const valueClass =
    tone === 'emerald'
      ? 'text-emerald-600'
      : tone === 'amber'
        ? 'text-amber-600'
        : tone === 'red'
          ? 'text-red-600'
          : 'text-slate-900';
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5 sm:py-3">
      <div className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400">
        {label}
      </div>
      <div className={`mt-1 font-mono-num text-[22px] sm:text-[26px] font-semibold tabular-nums leading-none ${valueClass}`}>
        {value}
      </div>
    </div>
  );
}
